import { forwardRef, useEffect, useState } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import { Box } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import ImageList from '@mui/material/ImageList';
import ImageListItem from '@mui/material/ImageListItem';
import Slide from '@mui/material/Slide';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { TransitionProps } from '@mui/material/transitions';
import AWS from 'aws-sdk';
import { isEmpty } from 'lodash';
import { useMutation, useQuery } from 'urql';

import {
  ApprovalStatus,
  LeaseInput,
  PaymentStatus,
  Property,
  SignatureStatus,
} from '../../../Graphql/Generated/graphql';
import { LIKE_PROPERTY, UNLIKE_PROPERTY } from '../../../Graphql/mutations';
import { IS_PROPERTY_LIKED } from '../../../Graphql/queries';
import { useAppDispatch, useAppSelector } from '../../../Hooks/useApp';
import { setAlert } from '../../../State/slices/alert';
import {
  selectCurrentUser,
  useLazyGetUserProfileQuery,
} from '../../../State/slices/authentication';
import { selectActiveLease, useCreateLeaseMutation } from '../../../State/slices/lease';
import {
  currentListing,
  sendInquiry,
  useCreateReservationMutation,
  useLazyGetPropertyByNameQuery,
} from '../../../State/slices/listing';
import { keywords } from '../../../Types/MetaData';
import { SignWellService } from '../../../Utils/Signwell/signwellService';
import { MESSAGE_TYPES } from '../../../Utils/constants';
import { capitalizeInitials } from '../../../Utils/nameFunctions';
import ImageLoader from '../../../legacy/Components/Generics/Image';
import { Loading } from '../../components/loading/Loading';
import { MessageModal } from '../../components/messageModal/MessageModal';
import { useIsMobile } from '../../hooks/useIsMobile';
import { SignDocumentModal } from '../myStays/components/SignDocumentModal';
import { BookingForm } from './components/BookingForm';
import { BookingFormMobile } from './components/BookingFormMobile';
import { BookingFormInputs } from './types';

const Container = styled('div')`
  width: 83%;
  margin: 5% auto;

  @media (max-width: 600px) {
    width: 100%;
    margin: 0;
  }
`;

const HeroSection = styled('div')`
  overflow: hidden;
  border-radius: 16px;
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  grid-gap: 10px;

  @media (max-width: 600px) {
    grid-template-rows: repeat(2, 1fr);
    grid-template-columns: repeat(2, 1fr);
  }
`;

const Title = styled(Typography)`
  @media (max-width: 600px) {
    font-size: 1.8rem;
  }
`;

const PropertyDetails = styled(Typography, {
  shouldForwardProp: (prop) => prop !== '$showmore',
})<{ $showmore: boolean }>`
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: ${(props) => (props.$showmore ? 100 : 3)};
  -webkit-box-orient: vertical;
`;

const GridImage = styled(ImageLoader)`
  width: 100%;
  height: 100%;
  object-fit: cover;
  &:first-of-type {
    grid-row-start: 1;
    grid-row-end: 3;
  }

  @media (max-width: 600px) {
    &:first-of-type {
      grid-column: span 2;
      grid-row: span 2;
    }
  }
`;

const ListingSection = styled('div')`
  display: flex;

  @media (max-width: 600px) {
    flex-direction: column;
  }
`;

const Description = styled(`div`)`
  width: 70%;
  padding-right: 45px;
  padding-top: 20px;

  @media (max-width: 600px) {
    width: 90%;
    padding-right: 0px;
    margin: auto;
  }
`;

const TitleSection = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PropertyDetailsSection = styled('div')`
  display: flex;
  justify-content: space-between;
`;

type TransitionPropsWithChildren = TransitionProps & {
  children: React.ReactElement<any, any>;
};

const Transition: React.FC<TransitionPropsWithChildren> = forwardRef((props, ref) => {
  return <Slide direction='up' ref={ref as any} {...props} />;
});

// Set the display name for the component
Transition.displayName = 'Transition';

type DocumentSignee = 'Landlord' | 'Tenant';

const signWellService = new SignWellService(process.env.REACT_APP_SIGNWELL_API_KEY || '');

//rental document template id
const DOCUMENT_TEMPLATE_ID = process.env.REACT_APP_SIGNWELL_DOCUMENT_TEMPLATE_ID || '';

export const BookingPage = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { propertyName } = useParams();
  const user = useAppSelector(selectCurrentUser);
  const activeLease = useAppSelector(selectActiveLease);
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  const propertyId = location?.state?.id || '';
  const [showMore, setShowMore] = useState(false);
  const [showAllImages, setShowAllImages] = useState(false);
  const [listing, setListing] = useState<Property>({} as Property);
  const [showAllAmenities, setShowAllAmenities] = useState(false);
  const [signLeaseDoc, setSignLeaseDoc] = useState<boolean>(false);
  const [signingDocumentUrl, setSigningDocumentUrl] = useState<string>('');
  const [getPropertyByName, { data: propertyData }] = useLazyGetPropertyByNameQuery();
  const [reservationLeaseId, setReservationLeaseId] = useState<string>('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [leaseInput, setLeaseInput] = useState<Partial<LeaseInput>>({});
  const [tenantEmbeddedUrl, setTenantEmbeddedUrl] = useState<string>('');
  const [documentId, setDocumentId] = useState<string>('');
  const [getUserProfile, { data: profile }] = useLazyGetUserProfileQuery();
  const [createLease] = useCreateLeaseMutation();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const getProfileAsync = async (userId: string) => {
    await getUserProfile({ userId });
  };

  const [createReservation, { isLoading: reservationLoading }] =
    useCreateReservationMutation();

  const [liked, setLiked] = useState(false);

  // TODO: Use Redux for likes here and on PropertyCard
  const likePropertyMutationVariables = {
    propertyId: propertyId,
    userId: user.id,
  };
  const [, likePlace] = useMutation(LIKE_PROPERTY);
  const [, unlikePlace] = useMutation(UNLIKE_PROPERTY);

  const handleLikedChange = () => {
    if (liked) {
      unlikePlace(likePropertyMutationVariables);
    } else {
      likePlace(likePropertyMutationVariables);
    }
    setLiked(!liked);
  };

  const [result] = useQuery({
    query: IS_PROPERTY_LIKED,
    variables: {
      propertyId: propertyId,
      userId: user.id,
    },
    pause: isEmpty(user.id),
  });

  useEffect(() => {
    if (!result.error && result.data) {
      setLiked(result.data.isPropertyLiked);
    }
  }, [result.error, result.data]);

  useEffect(() => {
    async function getProperty() {
      if (propertyName) {
        await getPropertyByName({ propertyName });
      }
    }

    getProperty();
  }, [propertyName, getPropertyByName]);

  useEffect(() => {
    if (propertyData?.getPropertyByName) setListing(propertyData.getPropertyByName);
  }, [propertyData]);

  useEffect(() => {
    try {
      if (listing?.ownerId) getProfileAsync(listing.ownerId);
    } catch (error) {
      console.log('an error occured');
    }
  }, [listing]);

  const {
    id,
    images = [],
    title,
    rooms,
    baths,
    name = '',
    description,
    amenities = [],
    securityDeposit,
  } = listing;
  const baseRent = listing.baseRent || 0;

  const dispatchAlert = (message: string, type: 'success' | 'error') => {
    dispatch(
      setAlert({
        message,
        type,
        isOpen: true,
      }),
    );
  };

  // @ts-ignore
  const [amenitiesToShow, setAmenitiesToShow] = useState([...amenities].slice(0, 10));

  useEffect(() => {
    if (amenities?.length) {
      if (showAllAmenities) setAmenitiesToShow([...amenities]);
      else setAmenitiesToShow([...amenities].slice(0, 10));
    }
  }, [showAllAmenities, amenities]);

  useEffect(() => {
    if (propertyData?.getPropertyByName) {
      setListing(propertyData.getPropertyByName);
      dispatch(currentListing(propertyData?.getPropertyByName));
    }
  }, [propertyData, dispatch]);

  //create the lease document signing url here
  const handleCreateLeaseDocument = async (leaseDocumentDetails: any) => {
    // Create and return the document
    //TODO: Once done with the document data; update the return to only the recipeints: document.recipients
    const document = await signWellService.createDocument(
      leaseDocumentDetails,
      DOCUMENT_TEMPLATE_ID,
    );

    return document;
  };

  const onSubmit = async (data: BookingFormInputs) => {
    setIsSubmitted(true);
    const termLength = data.stayDates.end?.compare(data.stayDates.start) + 1 || 30;
    const totalRent = ((baseRent || 0) / 30) * termLength;
    const grandTotal = totalRent + (securityDeposit || 0);

    //info to pre-populate the lease document while it's creating it
    const leaseDocumentDetails = {
      owner_name: `${profile?.getUserProfile?.firstName} ${profile?.getUserProfile?.lastName}`,
      owner_email: profile?.getUserProfile?.email,
      tenant_name: `${user?.firstName} ${user?.lastName}`,
      tenant_email: user?.email,
      address: `${listing.addressLine1},${listing.city}, ${listing.state}, ${listing.zipCode}`,
      lease_term: `${termLength} days`,
      lease_start_date: data.stayDates.start.toString(),
      lease_end_date: data.stayDates.end.toString(),
      totalCost: parseFloat(grandTotal.toString()).toFixed(2),
      securityDeposit: listing?.securityDeposit || '',
      cleaningFee: '', //moved to fill in by landlord, update signwell service file
    };

    //create lease doc
    const docResult = await handleCreateLeaseDocument(leaseDocumentDetails);

    // Extract the signing URLs from the document
    setTenantEmbeddedUrl(docResult.recipients[0].embedded_signing_url);
    setDocumentId(docResult.id);

    setLeaseInput({
      totalCost: grandTotal,
      startDate: data.stayDates.start.toString(),
      endDate: data.stayDates.end.toString(),
      bookingDate: new Date().toISOString(),
      reservationDate: new Date().toISOString(),
      tenantSigningUrl: docResult.recipients[0].embedded_signing_url,
      landlordSigningUrl: docResult.recipients[1].embedded_signing_url,
      leaseDocumentId: docResult.id,
      approvalStatus: ApprovalStatus.Pending ?? '',
      landlordSignature: SignatureStatus.Pending ?? '',
      paymentStatus: PaymentStatus.Pending ?? '',
      tenantSignature: SignatureStatus.Pending ?? '',
    });

    //open the modal for singing
    setSignLeaseDoc(true);

    const reservation = {
      propertyId: id,
      userId: user.id,
      start: data.stayDates.start.toString(),
      end: data.stayDates.end.toString(),
      room: '0',
      totalCost: grandTotal,
      paymentSchedule: '',
      message: data.message,
      phone: data.phoneNumber,
    };

    // Get the signature status from SignWell
    const signatureStatus = await signWellService.getDocumentStatus(documentId);

    if (signatureStatus[0].status === 'completed') {
      const response = await createReservation(reservation);

      //@ts-ignore
      if (response.data.createReservation === MESSAGE_TYPES.SUCCESS) {
        dispatchAlert('Reservation created successfully', 'success');
        dispatch(sendInquiry(listing));
      } else {
        dispatch(
          setAlert({
            message: 'Failed to create the reservation, please try again.',
            type: 'error',
            isOpen: true,
          }),
        );
        setIsSubmitted(false);
      }
    } else {
      setIsSubmitted(false);
      dispatch(
        setAlert({
          message:
            'Failed to create the reservation, Signature required on document. please try again.',
          type: 'error',
          isOpen: true,
        }),
      );
    }
  };

  const handleTenantSigning = async () => {
    try {
      // Get the signature status from SignWell
      const signatureStatus = await signWellService.getDocumentStatus(documentId);

      if (signatureStatus[0].status === 'completed') {
        // Create a lease input object including tenant signature
        const updatedLeaseInput = {
          ...leaseInput, // Retain existing lease input data
          tenantSignature: SignatureStatus.Signed, // Add tenant signature status
        };

        // Call the create lease mutation
        const response = await createLease({
          leaseInfo: updatedLeaseInput,
          propertyId: id,
          userId: user.id,
        });

        // @ts-ignore
        if (response.error) {
          dispatch(
            setAlert({
              message: `Error creating lease`,
              type: 'error',
              isOpen: true,
            }),
          );
          // @ts-ignore
        } else if (response.data) {
          dispatch(
            setAlert({
              message: 'Lease created successfully',
              type: 'success',
              isOpen: true,
            }),
          );

          navigate('/my-stays');
        }
      } else {
        // Handle cases where the signature is not completed
        setIsModalOpen(true);
        setIsSubmitted(false); //prevent book button from  becoming disabled

        dispatch(
          setAlert({
            message: 'Tenant signature is not yet completed. Please sign to continue.',
            type: 'warning',
            isOpen: true,
          }),
        );
      }
    } catch (error: any) {
      console.error('Create Lease Error:', error.message);
      dispatch(
        setAlert({
          message: `Error creating reservation and lease: ${error.message}`,
          type: 'error',
          isOpen: true,
        }),
      );
    }
  };

  const CAN_CREATE_RESERVATION =
    !isEmpty(activeLease) &&
    activeLease?.approvalStatus != ApprovalStatus.Pending &&
    activeLease?.approvalStatus != ApprovalStatus.InProgress;

  // Moved the SignDocumnetModal out, to avoid the bleeding from the form input label
  if (signLeaseDoc) {
    return (
      <SignDocumentModal
        userId={user.id}
        documentURL={tenantEmbeddedUrl}
        signee={'Tenant'}
        setShow={setSignLeaseDoc}
        onUpdate={handleTenantSigning}
      />
    );
  }

  // TODO: Map toggle option with a right drawer...
  // TODO: Add a little more to the page with icons and colors...
  return listing?.id ? (
    <>
      <HelmetProvider>
        <Helmet>
          <title>
            {listing?.title && listing?.title?.length < 65
              ? listing?.title
              : `${listing?.title?.substring(0, 61)}...`}
          </title>
          <meta name='description' content={listing?.description?.substring(0, 169)} />
          <meta name='keywords' content={keywords} />
          <link rel='canonical' href={`https://flxlease.com/property/${propertyName}`} />
        </Helmet>
      </HelmetProvider>

      <Container>
        <MessageModal
          title='Lease signature required'
          isOpen={isModalOpen}
          type='error'
          actionButtons={
            <Box sx={{ '& button': { m: 1 } }}>
              <Button
                color='primary'
                variant='outlined'
                onClick={() => setSignLeaseDoc(true)}
                size='small'
              >
                Stay and sign
              </Button>
              <Button
                color='primary'
                variant='contained'
                onClick={() => setIsModalOpen(false)}
                size='small'
              >
                Leave without saving
              </Button>
            </Box>
          }
          onClose={() => setIsModalOpen(false)}
        >
          <Typography>
            Tenant signature is required on lease agreement before reservation can be made.
            Leaving without signing will result in the loss of your current progress.
          </Typography>
        </MessageModal>

        {isMobile ? (
          <Box sx={{ width: '100%', padding: 0, height: '300px' }}>
            <Carousel showThumbs={false}>
              {images
                ?.slice(0, 5)
                .map((image: string, index: number) => (
                  <GridImage key={index} width='100%' height={300} filename={image || ''} />
                ))}
            </Carousel>
          </Box>
        ) : (
          <HeroSection>
            {images
              ?.slice(0, 5)
              .map((image: string, index: number) => (
                <GridImage key={index} width='100%' height='100%' filename={image || ''} />
              ))}
          </HeroSection>
        )}
        <ListingSection>
          {/* TODO: Match top alignment with booking form... */}
          <Description>
            <TitleSection>
              <Title variant='h1' as='h1' fontWeight='bold'>
                {title}
              </Title>
              <Avatar alt={name || ''} sx={{ marginRight: '10px' }}>
                {capitalizeInitials(name || '')}
              </Avatar>
            </TitleSection>
            <PropertyDetailsSection>
              <Typography
                color='text.secondary'
                component='h2'
              >{`${rooms} bedroom(s) • ${baths} bath(s)`}</Typography>
              <div>
                <Button variant='text' onClick={() => setShowAllImages(true)}>
                  <ImageOutlinedIcon sx={{ paddingRight: '5px' }} />
                  Show all images
                </Button>
                <Button variant='text' onClick={() => handleLikedChange()}>
                  {/* TODO: */}
                  {liked ? (
                    <FavoriteIcon sx={{ paddingRight: '5px' }} />
                  ) : (
                    <FavoriteBorderIcon sx={{ paddingRight: '5px' }} />
                  )}
                  {liked ? 'Unsave' : 'Save'}
                </Button>
              </div>
            </PropertyDetailsSection>
            <Divider sx={{ marginTop: '20px' }} />
            <Typography
              variant='h6'
              component='h6'
              fontWeight='bold'
              sx={{ marginTop: '20px' }}
            >
              What this location offers
            </Typography>
            {/* TODO: Animation */}
            <PropertyDetails variant='subtitle2' $showmore={showMore} as='p'>
              {description}
            </PropertyDetails>
            <Typography
              variant='caption'
              fontWeight='bold'
              sx={{ cursor: 'pointer' }}
              onClick={() => setShowMore((prev) => !prev)}
            >
              {showMore ? 'Show Less' : 'Show More'}
            </Typography>
            <Divider sx={{ marginTop: '20px' }} />
            <Typography
              variant='h6'
              component='h6'
              fontWeight='bold'
              sx={{ marginTop: '20px' }}
            >
              Amenities
            </Typography>
            <Grid container spacing={2} sx={{ marginTop: '20px', marginBottom: '25px' }}>
              {amenitiesToShow?.map((amenity, index) => (
                <Grid item xs={6} key={index}>
                  <Typography variant='body1'>{amenity}</Typography>
                </Grid>
              ))}
            </Grid>
            <Button
              color='secondary'
              onClick={() => setShowAllAmenities((prev) => !prev)}
              variant='outlined'
            >
              {showAllAmenities ? 'Show less' : `Show all ${amenities?.length} amenities`}
            </Button>
          </Description>
          {!isMobile && (
            <>
              <BookingForm
                listing={listing}
                onSubmit={onSubmit}
                isSubmitting={reservationLoading}
                isSubmitted={isSubmitted}
                disabled={!CAN_CREATE_RESERVATION}
              />
              <Dialog
                open={showAllImages}
                TransitionComponent={Transition}
                keepMounted
                onClose={() => setShowAllImages(false)}
                aria-describedby='alert-dialog-slide-description'
              >
                <DialogContent>
                  <ImageList cols={1}>
                    {/* @ts-ignore */}
                    {images?.map((image: string, index: number) => (
                      <ImageListItem key={index}>
                        <ImageLoader
                          width='100%'
                          height='100%'
                          filename={image}
                          loading='eager'
                        />
                      </ImageListItem>
                    ))}
                  </ImageList>
                </DialogContent>
              </Dialog>
            </>
          )}
        </ListingSection>
      </Container>
      {isMobile && (
        <BookingFormMobile
          listing={listing}
          onSubmit={onSubmit}
          isSubmitting={reservationLoading}
          isSubmitted={isSubmitted}
        />
      )}
    </>
  ) : (
    <Loading isLoading={!listing} />
  );
};

export default BookingPage;
